import { ContentDetailSummary } from '@raydiant/api-client-js';
import { BarChart, LineChart } from 'echarts/charts';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { useTranslation } from 'react-i18next';
import Hidden from 'raydiant-elements/layout/Hidden';
import { renderToStaticMarkup } from 'react-dom/server';
import { Popover } from '@headlessui/react';

import {
  ageGroups,
  averageDataLineColor,
  colorsForContentDetailCharts,
  getViewingByDemographicData,
  iconForLegend,
} from './utils';
import { CHART_DEFAULT_AXIS_OPTIONS, CHART_DEFAULT_OPTIONS } from '../utils';
import ChartToolTip, { EChartsToolTipParams } from '../ChartToolTip';
import ChartCard from '../ChartCard/ChartCard';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import config from '../../../config';

const { supportAppUrl } = config;

echarts.use([BarChart, LineChart]);

const color = [...colorsForContentDetailCharts, averageDataLineColor];

type AverageViewByDemographicChartProps = {
  contentData: ContentDetailSummary;
};

export default function AverageViewByDemographicChart({
  contentData,
}: AverageViewByDemographicChartProps) {
  const { t } = useTranslation();
  const { male, female } = getViewingByDemographicData(contentData);

  const maleData = male.map((value) => Number(value).toFixed(2));
  const femaleData = female.map((value) => Number(value).toFixed(2));
  const averageContent = Number(contentData.allAvgViewDuration).toFixed(2);
  const viewDurationMale = Number(contentData.avgViewingDurationMale).toFixed(
    2,
  );
  const viewDurationFemale = Number(
    contentData.avgViewingDurationFemale,
  ).toFixed(2);
  const isEmptyData = !Number(viewDurationMale) && !Number(viewDurationFemale);

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      top: 50,
    },
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: EChartsToolTipParams[]) => {
        const modifiedParams: EChartsToolTipParams[] = params.map((item) => ({
          ...item,
          value: `${item.value}s`,
        }));

        modifiedParams.push({
          seriesName: t(
            'analytics.contentDetailPage.viewByDemographic.averageContent',
          ),
          value: `${averageContent}s`,
          color: averageDataLineColor,
          borderColor: '',
          dataIndex: 0,
          name: '',
          data: [],
        });

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params[0].axisValueLabel}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      icon: iconForLegend,
    },
    color: color,
    xAxis: {
      ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
      type: 'value',
      name: t(
        'analytics.contentDetailPage.viewByDemographic.xAxisTitle',
      ).toUpperCase(),
      axisLabel: {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
        formatter: (value: string) => `${value}s`,
      },
    },
    yAxis: {
      ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
      type: 'category',
      data: ageGroups,
      name: t(
        'analytics.contentDetailPage.viewByDemographic.yAxisTitle',
      ).toUpperCase(),
      axisLabel: {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
        alignTicks: true,
        formatter: (value: string) => value,
      },
      nameGap: 40,
    },
    series: [
      {
        name: `Male (${viewDurationMale}s)`,
        type: 'bar',
        emphasis: {
          focus: 'series',
        },
        data: maleData,
      },
      {
        name: `Female (${viewDurationFemale}s)`,
        type: 'bar',
        emphasis: {
          focus: 'series',
        },
        data: femaleData,
      },
      {
        type: 'line',
        markLine: {
          symbol: 'none',
          silent: true,
          data: [
            {
              xAxis: averageContent,
              label: {
                formatter: t(
                  'analytics.contentDetailPage.viewByDemographic.averageContent',
                ),
                color: averageDataLineColor,
              },
            },
          ],
        },
      },
    ],
  };

  const mobileOptions = {
    ...options,
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      icon: iconForLegend,
      type: 'scroll',
    },
  };

  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center">
          <h2 className="text-xl font-bold text-black">
            {t('analytics.contentDetailPage.viewByDemographic.title')}
          </h2>
          <Popover className="relative">
            <Popover.Button>
              <QuestionMarkIcon />
            </Popover.Button>
            <Popover.Panel className="absolute z-10 card popover">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {t('analytics.contentDetailPage.viewByDemographic.tooltip')}
                </p>
                <a
                  href={`${supportAppUrl}/s/article/How-do-I-use-Raydiant-Analytics`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
      showTimeInterval={false}
    >
      <div className="min-w-full min-h-full">
        {isEmptyData ? (
          <div className="w-full text-center">
            {t('analytics.contentDetailPage.emptyData.emptyChartData')}
          </div>
        ) : (
          <>
            <Hidden xsDown>
              <ReactECharts
                style={{ height: '460px' }}
                option={options}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
            <Hidden smUp>
              <ReactECharts
                style={{ height: '460px' }}
                option={mobileOptions}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
          </>
        )}
      </div>
    </ChartCard>
  );
}
